import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { styled, cn } from '@/lib/utils'

export const Header = styled('div')(
  'px-4 py-2 bg-background',
  'w-[calc(100%_+_2px)] flex items-center gap-4',
  'border -mx-[1px] rounded-t-lg'
)

interface LogViewerWrapperProps {
  hasContentAbove?: boolean
  className?: string
}

export const LogViewerWrapper = ({
  hasContentAbove,
  className,
  ...props
}: LogViewerWrapperProps & React.ComponentProps<'div'>) => (
  <div
    className={cn(
      'relative font-mono text-muted-foreground bg-muted p-0',
      !hasContentAbove ? 'rounded-t-lg' : 'border-t-0',
      'rounded-b-lg',
      className
    )}
    {...props}
  />
)

export const ConsoleContent = styled('span')('px-2')

type LinePartProps = {
  color?: string
  background?: string
  bold?: boolean
  className?: string
}

export const LinePart = ({
  color,
  background,
  bold,
  className,
  style,
  ...props
}: LinePartProps & React.ComponentProps<'span'>) => (
  <span
    className={cn(bold ? 'font-semibold' : 'font-medium', 'text-xs', className)}
    style={{
      color: color ?? 'var(--foreground)',
      background: background ?? 'inherit',
      ...style,
    }}
    {...props}
  />
)

export const LogMessage = styled('div')(
  'min-w-0 flex-1 text-xs whitespace-pre-wrap [overflow-wrap:anywhere]'
)

type LogRowContentProps = {
  isHighlighted?: boolean
  isFirstLine?: boolean
  isLastLine?: boolean
  className?: string
}

export const LogRowContent = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isHighlighted,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isFirstLine = false,
  isLastLine = false,
  className,
  ...props
}: LogRowContentProps & React.ComponentProps<'div'>) => (
  <div
    className={cn(
      'relative px-2 py-0.5 grid grid-cols-[minmax(3rem,auto)_1fr] gap-2',
      '[&>span:last-of-type]:whitespace-pre-wrap [&>span:last-of-type]:min-w-0',
      'bg-background border-b border-border/40',
      isLastLine ? 'border-b-0' : undefined,
      'hover:bg-muted/50',
      className
    )}
    {...props}
  />
)

type ResumeNotePanelParams = {
  isVisible: boolean
  className?: string
}

export const ResumeNotePanel = ({
  isVisible,
  className,
  children,
  ...props
}: ResumeNotePanelParams & React.ComponentProps<typeof Alert>) => (
  <div
    className={cn(
      'transition-all duration-200 linear border-t-1',
      'flex items-center justify-between px-4 py-3 bg-background',
      isVisible ? 'opacity-100 visible' : 'opacity-0 invisible',
      className
    )}
    {...props}
  >
    {children}
  </div>
)

type ToBottomButtonParams = {
  isVisible: boolean
  className?: string
}

export const ToBottomButton = ({
  isVisible,
  className,
  ...props
}: ToBottomButtonParams & React.ComponentProps<typeof Button>) => (
  <Button
    size="icon"
    className={cn(
      'absolute right-8 bottom-8 transition-all shadow-md',
      isVisible ? 'opacity-100 visible' : 'opacity-0 invisible',
      className
    )}
    {...props}
  />
)
